import { useCallback, useMemo, useRef, useState } from "react";

import { offset } from "@floating-ui/dom";
import {
  faChevronDown,
  faFile,
  faFiles,
  faUpload,
} from "@fortawesome/pro-regular-svg-icons";
import Dropzone, { DropzoneRef, FileRejection } from "react-dropzone";
import { useFormContext, useWatch } from "react-hook-form";

import { useUploadFile } from "src/api/filesApi/queries";
import { Button } from "src/base-components/Button";
import { Card } from "src/base-components/Card";
import { FileCard } from "src/base-components/FileCard";
import { OptionsDropdown } from "src/base-components/OptionsDropdown/OptionsDropdown";
import { type OptionsDropdownElement } from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { Pill } from "src/base-components/Pill";
import { CaseFileCard } from "src/cases/caseView/CaseFileCard";
import { type FileCategoryBase } from "src/cases/caseView/FileCategory";
import { CreateCaseFormValues } from "src/cases/createCase/createCaseSteps";
import { UploadFileEnvFilesPostEnvEnum } from "src/clients/files";
import { Accordion } from "src/design-system/Accordion";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon } from "src/design-system/Icon";
import { toastActions } from "src/design-system/Toast/utils";
import { useEnvironment } from "src/hooks/useEnvironment";
import {
  CASE_FILE_ACCEPTED_FORMATS,
  DEFAULT_ACCEPTED_FILE_TYPES,
  getCaseFileUploadErrorMessage,
  MAX_FILE_SIZE_BYTES,
} from "src/utils/files";

const CREATE_CASE_FILE_VIEWER_POSITION = {
  placement: "left-start" as const,
  middleware: [offset({ mainAxis: -300, crossAxis: 0 })],
};

type CreateCaseFileUploadProps = {
  fileCategories: FileCategoryBase[];
};

export const CreateCaseFileUpload: React.FC<CreateCaseFileUploadProps> = ({
  fileCategories,
}) => {
  const dropzoneRef = useRef<DropzoneRef>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [pendingCategory, setPendingCategory] = useState<string | null>(null);
  const [openAccordionItems, setOpenAccordionItems] = useState<string[]>([]);
  const { onFileUpload } = useUploadFile();
  const form = useFormContext<CreateCaseFormValues>();
  const [environment] = useEnvironment();

  const file_category_files = useWatch({
    control: form.control,
    name: "file_category_files",
  });

  const uploadEnv =
    environment === "sandbox"
      ? UploadFileEnvFilesPostEnvEnum.SANDBOX
      : UploadFileEnvFilesPostEnvEnum.LIVE;

  const handleCategorySelect = useCallback((categoryKey: string) => {
    setPendingCategory(categoryKey);
    dropzoneRef.current?.open();
  }, []);

  const dropdownElements = useMemo(
    (): OptionsDropdownElement[] => [
      {
        key: "header",
        header: "Add new file to:",
      },
      ...fileCategories.map((cat) => ({
        key: cat.display_name,
        action: () => handleCategorySelect(cat.category_key),
      })),
    ],
    [fileCategories, handleCategorySelect],
  );

  const handleFileUploadWithCategory = useCallback(
    async (file: File, categoryKey: string) => {
      setIsUploading(true);

      try {
        const fileId = await onFileUpload(
          file,
          `create-case-${categoryKey}-${Date.now()}`,
          uploadEnv,
          "case-files",
        );

        if (fileId) {
          const current = form.getValues("file_category_files");
          const updatedCategoryFiles = [
            ...(current[categoryKey] || []),
            fileId,
          ];
          form.setValue("file_category_files", {
            ...current,
            [categoryKey]: updatedCategoryFiles,
          });

          setOpenAccordionItems((prev) =>
            prev.includes(categoryKey) ? prev : [...prev, categoryKey],
          );
        } else {
          toastActions.failure({
            title: `Upload failed for ${file.name}. Please try again.`,
          });
        }
      } catch {
        toastActions.failure({
          title: `Upload failed for ${file.name}. Please try again.`,
        });
      } finally {
        setIsUploading(false);
        setPendingCategory(null);
      }
    },
    [onFileUpload, uploadEnv, form],
  );

  const handleFileSelect = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (acceptedFiles.length > 0 && pendingCategory) {
        handleFileUploadWithCategory(acceptedFiles[0], pendingCategory);
        return;
      }

      if (rejectedFiles.length > 0) {
        const firstRejection = rejectedFiles[0];
        const errorCode = firstRejection.errors[0]?.code;
        toastActions.failure({
          title: getCaseFileUploadErrorMessage(
            errorCode,
            firstRejection.file.name,
          ),
        });
      }
    },
    [pendingCategory, handleFileUploadWithCategory],
  );

  const handleDeleteFile = useCallback(
    (fileId: string, categoryKey: string) => {
      const current = form.getValues("file_category_files");
      const updatedCategoryFiles = (current[categoryKey] || []).filter(
        (id) => id !== fileId,
      );
      form.setValue("file_category_files", {
        ...current,
        [categoryKey]: updatedCategoryFiles,
      });

      if (updatedCategoryFiles.length === 0) {
        setOpenAccordionItems((prev) =>
          prev.filter((key) => key !== categoryKey),
        );
      }
    },
    [form],
  );

  if (fileCategories.length === 0) {
    return (
      <EmptyState
        description="No file categories have been configured for this case template"
        headline="No file categories"
        icon={faFiles}
        variant="default"
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <Card className="rounded-lg border-dashed p-4" variant="inverted">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-indigo-100">
            <Icon icon={faUpload} size="md" variant="active" />
          </div>
          <div className="flex-1">
            <h3 className="font-inter-semibold-13px text-gray-800">
              Click here to upload
            </h3>
            <p className="mt-1 font-inter-normal-12px text-gray-500">
              Supported types: {CASE_FILE_ACCEPTED_FORMATS.join(", ")}
            </p>
          </div>
          <OptionsDropdown
            buttonDataLoc="create-case-upload-file-dropdown"
            elements={dropdownElements}
            placement="bottom start"
            disabled={isUploading}
          >
            <Button
              size="sm"
              variant="secondary"
              iconRight={faChevronDown}
              disabled={isUploading}
            >
              {isUploading ? "Uploading..." : "Upload a file"}
            </Button>
          </OptionsDropdown>
        </div>
      </Card>

      <Accordion value={openAccordionItems} onChange={setOpenAccordionItems}>
        {fileCategories.map((category) => {
          const fileIds = file_category_files[category.category_key] || [];
          return (
            <Accordion.Item
              key={category.category_key}
              isReadOnly={fileIds.length === 0}
              rightSlot={
                <Pill size="sm" variant="indigo-light">
                  <Pill.Icon icon={faFile} />
                  <Pill.Text>{fileIds.length}</Pill.Text>
                </Pill>
              }
              label={category.display_name}
              value={category.category_key}
            >
              <div className="flex flex-col gap-3">
                {fileIds.map((fileId) => (
                  <CaseFileCard
                    key={fileId}
                    fileId={fileId}
                    positionOptions={CREATE_CASE_FILE_VIEWER_POSITION}
                  >
                    {(onView) => (
                      <FileCard
                        dataLoc={`create-case-file-${fileId}`}
                        fileEnv={environment}
                        fileId={fileId}
                        onDelete={() =>
                          handleDeleteFile(fileId, category.category_key)
                        }
                        onView={onView}
                      />
                    )}
                  </CaseFileCard>
                ))}
              </div>
            </Accordion.Item>
          );
        })}
      </Accordion>

      <div className="hidden">
        <Dropzone
          ref={dropzoneRef}
          accept={DEFAULT_ACCEPTED_FILE_TYPES}
          disabled={isUploading}
          maxFiles={1}
          maxSize={MAX_FILE_SIZE_BYTES}
          noClick
          noDrag
          onDrop={handleFileSelect}
        >
          {({ getRootProps, getInputProps }) => (
            <div {...getRootProps()}>
              <input {...getInputProps()} />
            </div>
          )}
        </Dropzone>
      </div>
    </div>
  );
};
